import React, { useState } from "react";
import {
  useOfferAffiliates, useAssignAffiliate, useUpdateOfferAffiliate, useRemoveOfferAffiliate,
} from "@/hooks/use-offers";
import { useAffiliates } from "@/hooks/use-affiliates";
import { Card } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, Users, ChevronDown, Check, X, Pencil } from "lucide-react";

interface Props {
  offerId: string;
}

export function OfferAffiliatesTab({ offerId }: Props) {
  const { data, isLoading } = useOfferAffiliates(offerId);
  const assignMutation = useAssignAffiliate(offerId);
  const updateMutation = useUpdateOfferAffiliate(offerId);
  const removeMutation = useRemoveOfferAffiliate(offerId);
  const [showForm, setShowForm] = useState(false);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState("");
  const [error, setError] = useState("");

  // Fetch all affiliates for the dropdown
  const { data: affData } = useAffiliates({ pageSize: 500 });
  const allAffiliates = affData?.data ?? [];

  const assigned = data?.data ?? [];

  // Filter out already assigned affiliates
  const assignedIds = new Set(assigned.map((a: { affiliateId: number }) => a.affiliateId));
  const availableAffiliates = allAffiliates.filter((a: { id: number }) => !assignedIds.has(a.id));

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!selectedAffiliateId) return;
    try {
      await assignMutation.mutateAsync({ affiliateId: Number(selectedAffiliateId) });
      setSelectedAffiliateId("");
      setShowForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to assign affiliate");
    }
  }

  async function handleRemove(assignmentId: number, name: string) {
    if (!window.confirm(`Remove "${name}" from this offer?`)) return;
    await removeMutation.mutateAsync(assignmentId);
  }

  async function handleStatusChange(assignmentId: number, newStatus: string) {
    await updateMutation.mutateAsync({ assignmentId, status: newStatus });
  }

  async function handleApprovalChange(assignmentId: number, newApproval: string) {
    await updateMutation.mutateAsync({ assignmentId, approvalStatus: newApproval });
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-100 rounded-xl" />
        <div className="h-24 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Assigned Affiliates</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage which affiliates can run this offer and generate tracking links.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Assign Affiliate
        </button>
      </div>

      {showForm && (
        <Card>
          <form onSubmit={handleAssign} className="space-y-3">
            {error && (
              <div className="bg-red-50 text-red-700 text-xs p-2 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Select Affiliate</label>
              <select
                required
                value={selectedAffiliateId}
                onChange={(e) => setSelectedAffiliateId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Choose an affiliate...</option>
                {availableAffiliates.map((a: { id: number; name: string }) => (
                  <option key={a.id} value={a.id}>
                    #{a.id} - {a.name}
                  </option>
                ))}
              </select>
              {availableAffiliates.length === 0 && (
                <p className="text-xs text-gray-400 mt-1">All affiliates are already assigned.</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setError(""); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={assignMutation.isPending || !selectedAffiliateId}
                className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {assignMutation.isPending ? "Assigning..." : "Assign"}
              </button>
            </div>
          </form>
        </Card>
      )}

      {assigned.length === 0 && !showForm ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No affiliates assigned</p>
          <p className="text-xs text-gray-400 mt-1">Assign affiliates to generate tracking links for this offer</p>
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-xl">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Payout Override</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Revenue Override</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {assigned.map((aff: {
                id: number;
                affiliateId: number;
                affiliateName: string;
                status: string;
                approvalStatus: string;
                payoutOverride: number | null;
                revenueOverride: number | null;
              }) => (
                <tr key={aff.id}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{aff.affiliateName}</div>
                    <div className="text-xs text-gray-400">ID: {aff.affiliateId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={aff.status}
                      onChange={(e) => handleStatusChange(aff.id, e.target.value)}
                      className="text-xs rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={aff.approvalStatus}
                      onChange={(e) => handleApprovalChange(aff.id, e.target.value)}
                      className="text-xs rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900"
                    >
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <InlineOverride
                      label="Payout"
                      value={aff.payoutOverride}
                      onSave={(val) => updateMutation.mutateAsync({ assignmentId: aff.id, payoutOverride: val })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <InlineOverride
                      label="Revenue"
                      value={aff.revenueOverride}
                      onSave={(val) => updateMutation.mutateAsync({ assignmentId: aff.id, revenueOverride: val })}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleRemove(aff.id, aff.affiliateName)}
                      disabled={removeMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InlineOverride({ label, value, onSave }: { label: string; value: number | null; onSave: (val: number | null) => Promise<unknown> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value != null ? String(value) : "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const num = draft.trim() === "" ? null : Number(draft);
      await onSave(num);
      setEditing(false);
    } finally { setSaving(false); }
  }

  if (!editing) {
    return (
      <button
        onClick={() => { setDraft(value != null ? String(value) : ""); setEditing(true); }}
        className="group inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        {value != null ? `$${value.toFixed(2)}` : <span className="text-gray-400">--</span>}
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step="0.01"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        className="w-20 text-sm rounded border border-gray-300 px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-gray-900"
        placeholder="$0.00"
        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
      />
      <button onClick={handleSave} disabled={saving} className="p-0.5 text-green-600 hover:bg-green-50 rounded">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => setEditing(false)} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
